/**
 * Pricing-email renderer — turns a saved review's boilerplate into the per-customer
 * email text shown in the preview and copied to Outlook.
 *
 * The boilerplate supports `{{var}}` placeholders (same grammar as the quote
 * template renderer) plus a single `{{increasesTable}}` marker, which is replaced
 * with a table of the customer's *increasing* products (current vs new price,
 * GST-exclusive). Only products that actually increase are included — matching the
 * brief ("only the product increases relevant to this customer").
 *
 * Produces both an HTML variant (faithful preview + rich Outlook paste) and a
 * plain-text variant (degraded paste targets), mirroring QuoteHtmlPreviewDialog.
 */

import type { PricingReviewCustomerRow, PricingReviewLineRow } from '@techgitter/contracts';

export const INCREASES_TABLE_TOKEN = '{{increasesTable}}';

/** Variables the editor advertises as available in the boilerplate. */
export const PRICING_EMAIL_VARIABLES: ReadonlyArray<{ token: string; label: string }> = [
  { token: '{{customerName}}', label: 'Customer name' },
  { token: '{{contactName}}', label: 'Contact name (falls back to customer name)' },
  { token: '{{increaseCount}}', label: 'Number of products increasing' },
  { token: INCREASES_TABLE_TOKEN, label: 'Table of price increases (inserted automatically)' },
];

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });

/** Increasing lines only — the ones the customer is told about. */
export function increasingLines(customer: PricingReviewCustomerRow): PricingReviewLineRow[] {
  return customer.lines.filter((l) => l.isIncrease && l.newSell !== null);
}

function buildTableHtml(lines: PricingReviewLineRow[]): string {
  const cell =
    'font-family:Calibri,Arial,sans-serif;padding:6px 10px;font-size:11pt;color:#333;border-bottom:1px solid #eee;';
  const head =
    'font-family:Calibri,Arial,sans-serif;padding:6px 10px;font-size:11pt;font-weight:bold;color:#000;text-align:left;border-bottom:2px solid #ccc;';
  const rows = lines
    .map((l) => {
      const name = escapeHtml(l.productName || l.sku);
      const current = fmt(l.currentSell);
      const next = l.newSell !== null ? fmt(l.newSell) : '';
      return `
      <tr>
        <td style="${cell}">${name}</td>
        <td style="text-align:right;white-space:nowrap;${cell}">${current}</td>
        <td style="text-align:right;white-space:nowrap;${cell}">${next}</td>
      </tr>`;
    })
    .join('');
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:65%;table-layout:fixed;border:1px solid #ccc;border-collapse:collapse;border-radius:4px;margin:12px 0;">
      <tr>
        <th style="${head}width:60%;">Product</th>
        <th style="${head}text-align:right;width:20%;">Current Price*</th>
        <th style="${head}text-align:right;width:20%;">New Price*</th>
      </tr>
      ${rows}
    </table>
    <div style="font-family:Calibri,Arial,sans-serif;font-size:9pt;color:#888;margin:4px 0 12px;">* All prices ex GST</div>`;
}

function buildTablePlain(lines: PricingReviewLineRow[]): string {
  const rows = lines
    .map((l) => {
      const name = l.productName || l.sku;
      const current = fmt(l.currentSell);
      const next = l.newSell !== null ? fmt(l.newSell) : '';
      return `- ${name}: ${current} -> ${next}`;
    })
    .join('\n');
  return `${rows}\n* All prices ex GST`;
}

function valuesFor(customer: PricingReviewCustomerRow): Record<string, string> {
  const name = customer.customerName || '';
  return {
    customerName: name,
    contactName: name,
    increaseCount: String(increasingLines(customer).length),
  };
}

/** Replace `{{path}}` tokens (excluding the increases-table marker, handled separately). */
function interpolate(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) =>
    key === 'increasesTable' ? _m : (values[key] ?? ''),
  );
}

export interface RenderedPricingEmail {
  subject: string;
  html: string;
  plain: string;
}

/**
 * Render a customer's pricing email from the boilerplate. The body's
 * `{{increasesTable}}` marker is replaced with the customer's increases (HTML or
 * plain depending on the variant); other `{{var}}` tokens are interpolated.
 */
export function renderPricingEmail(
  subjectTemplate: string,
  bodyTemplate: string,
  customer: PricingReviewCustomerRow,
): RenderedPricingEmail {
  const values = valuesFor(customer);
  const lines = increasingLines(customer);

  const subject = interpolate(subjectTemplate || '', values);

  const segments = (bodyTemplate || '').split(INCREASES_TABLE_TOKEN);
  const last = segments.length - 1;
  // Drop newlines that sit immediately against the table so there's no empty
  // line above it or below the footnote — the table/footnote margins provide
  // the spacing. (HTML drops them entirely; plain text keeps a single break.)
  const trimForHtml = (seg: string, i: number): string => {
    let s = seg;
    if (i > 0) s = s.replace(/^\n+/, ''); // after the table/footnote
    if (i < last) s = s.replace(/\n+$/, ''); // before the table
    return s;
  };
  const trimForPlain = (seg: string, i: number): string => {
    let s = seg;
    if (i > 0) s = s.replace(/^\n+/, '\n');
    if (i < last) s = s.replace(/\n+$/, '\n');
    return s;
  };

  // --- HTML: escape + <br> the prose segments, drop the rendered table between. ---
  const tableHtml = lines.length ? buildTableHtml(lines) : '';
  const htmlBody = segments
    .map((seg, i) => escapeHtml(interpolate(trimForHtml(seg, i), values)).replace(/\n/g, '<br />'))
    .join(tableHtml);
  const html = `<div style="font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#333;line-height:1.5;">${htmlBody}</div>`;

  // --- Plain text: interpolate then swap the marker for the plain table. ---
  const tablePlain = lines.length ? buildTablePlain(lines) : '';
  const plain = segments
    .map((seg, i) => interpolate(trimForPlain(seg, i), values))
    .join(tablePlain);

  return { subject, html, plain };
}
